import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import { EventEmitter } from '@angular/core';
import { SpaContext } from './spacontext';
import { VisibaPopup } from '../Common/Classes/visibaPopup';
import { v4 } from '../Common/Helpers/v4';
import { SpaLogger } from '../Common/Classes/Global/spalogger.class';

export abstract class UserMedia {
	public mediaStream: MediaStream = null;
	public chosenVideoinputDeviceId = 'default';
	public chosenAudioinputDeviceId = 'default';
	public chosenAudiooutputDeviceId = 'default';

	// Check which ones that are really needed.
	public onUpdateMediaDevice = new EventEmitter<'videoinput' | 'audioinput' | 'audiooutput'>();
	public onUpdateActiveDevices = new EventEmitter<{
		device: 'videoinput' | 'audioinput' | 'audiooutput';
		state: 'added' | 'removed';
	}>();
	public onDeviceChange = new EventEmitter<void>();
	public onLocalTrackUpdated = new EventEmitter<MediaStreamTrack>();

	public devices = {
		videoinput: [],
		audioinput: [],
		audiooutput: [],
	};
	private activePopup: VisibaPopup;
	private popupList: Array<VisibaPopup> = [];
	private _streamSessionID: string = v4();
	get streamSessionID(): string {
		return this._streamSessionID;
	}
	set streamSessionID(ID: string) {
		this._streamSessionID = ID;
	}

	constructor(public _spaLogger: SpaLogger, public _spaContext: SpaContext, public translateService: TranslateService) {
		if (!navigator.mediaDevices) return;

		navigator.mediaDevices.ondevicechange = () => this.deviceChange();
	}

	public newStreamSessionID() {
		this.streamSessionID = v4();
	}

	public getMediaStream(constraints): Promise<MediaStream> {
		return new Promise(async (resolve, reject) => {
			try {
				// Keeping the comment if it should be needed...
				// exactID & type will be removed when a better solution for the FireFox bug arrives.
				//let builder;
				//if (typeof exactID !== 'undefined' && exactID !== 'default') {
				//    if (type === 'video') {
				//        builder = {
				//            video: {
				//                width: { max: 1280 },
				//                height: { max: 720 },
				//                deviceId: { exact: exactID },
				//            },
				//            audio: false
				//        };
				//    } else {
				//        builder = {
				//            video: false,
				//            audio: {
				//                deviceId: { exact: exactID },
				//            },
				//        };
				//    }
				//} else {
				//    if (type === 'video') {
				//        builder = { video: true, audio: false };
				//    } else {
				//        builder = { audio: false, video: true };
				//    }
				//}

				const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

				if (mediaStream.getAudioTracks().length > 0) {
					this.chosenAudioinputDeviceId =
						typeof constraints.audio.deviceId !== 'undefined' ? constraints.audio.deviceId.exact : 'default';
				}

				if (mediaStream.getVideoTracks().length > 0) {
					this.chosenVideoinputDeviceId =
						typeof constraints.video.deviceId !== 'undefined' ? constraints.video.deviceId.exact : 'default';
				}

				this.newStreamSessionID();
				resolve(mediaStream);
			} catch (error) {
				reject(error);
			}
		});
	}

	public getDeviceList(): Promise<any> {
		return new Promise((resolve, reject) => {
			navigator.mediaDevices
				.enumerateDevices()
				.then((devices: Array<MediaDeviceInfo>) => {
					let listedDevices = {
						videoinput: [{ label: 'Default', deviceId: 'default', kind: 'videoinput' }],
						audioinput: [],
						audiooutput: [],
					};

					devices.forEach((device) =>
						listedDevices[`${device.kind}`].push({ label: device.label, deviceId: device.deviceId, kind: device.kind }),
					);

					listedDevices.videoinput.forEach((device, i) => {
						if (device.label === null || device.label === '') {
							device.label = `Camera ${i.toString()}`;
						}
					});
					listedDevices.audioinput.forEach((device, i) => {
						if (device.label === null || device.label === '') {
							device.label = `Microphone ${i.toString()}`;
						}
					});
					listedDevices.audiooutput.forEach((device, i) => {
						if (device.label === null || device.label === '') {
							device.label = `Sound output ${i.toString()}`;
						}
					});

					this._spaLogger.log('Debug', ['Media'], 'Devices', listedDevices);

					resolve(listedDevices);
				})
				.catch((error) => reject(this.parseError(error, 'Generic', false)));
		});
	}

	public updateVideoTrack(
		exactID: string | null = 'default',
		constraints?: MediaTrackConstraints,
		fromStream?: MediaStream,
	): Promise<{ oldTrack: MediaStreamTrack; newTrack: MediaStreamTrack }> {
		const builder = {
			audio: false,
			video: constraints || this.getCameraConstraints(exactID),
		};

		return new Promise(async (resolve, reject) => {
			try {
				const stream: MediaStream = fromStream || (await this.getMediaStream(builder));
				const newTrack: MediaStreamTrack = stream.getVideoTracks()[0];
				const oldTrack = this.mediaStream.getVideoTracks()[0];

				newTrack.enabled = oldTrack.enabled;

				oldTrack.onended = null;
				oldTrack.stop();

				this.mediaStream.removeTrack(oldTrack);
				this.mediaStream.addTrack(newTrack);
				this.onLocalTrackUpdated.emit(newTrack);

				resolve({ oldTrack: oldTrack, newTrack: newTrack });
			} catch (error) {
				reject(this.parseError(error, 'Video'));
			}
		});
	}

	public updateAudioTrack(
		exactID: string | null = 'default',
		constraints?: MediaTrackConstraints,
	): Promise<{ oldTrack: MediaStreamTrack; newTrack: MediaStreamTrack }> {
		const builder = {
			audio: constraints || this.getAudioConstraints(exactID),
			video: false,
		};

		return new Promise(async (resolve, reject) => {
			try {
				const oldTrack = this.mediaStream.getAudioTracks()[0];
				if (platform.name === 'Firefox')
					// Here for firefox, to prevent bug, sound will be abrupted
					this.removeOldAudioTrack(oldTrack);

				const stream: MediaStream = await this.getMediaStream(builder);
				const newTrack: MediaStreamTrack = stream.getAudioTracks()[0];

				newTrack.enabled = oldTrack.enabled;

				if (platform.name !== 'Firefox')
					// Here for non firefox, sound not abrupted
					this.removeOldAudioTrack(oldTrack);

				this.mediaStream.addTrack(newTrack);

				resolve({ oldTrack: oldTrack, newTrack: newTrack });
			} catch (error) {
				reject(this.parseError(error, 'Audio'));
			}
		});
	}

	private removeOldAudioTrack(track: MediaStreamTrack) {
		// 2018-10-09: Firefox cant access new audiostream unless the old is removed before.
		track.onended = null;
		track.stop();
		this.mediaStream.removeTrack(track);
	}

	public async attachSinkId(element, sinkId) {
		if (typeof element.sinkId !== 'undefined') {
			try {
				await element.setSinkId(sinkId);

				console.log('Success, audio output device attached');
			} catch (error) {
				this.parseError(error, 'SinkId');
			}
		} else {
			console.warn('Browser does not support output device selection.');
		}
	}

	public async deviceChange() {
		try {
			let status = '';
			let statusState = '';
			let deviceId = 'default';
			const updatedDevices = await this.getDeviceList();
			const [removed] = Object.keys(updatedDevices).filter((device) => updatedDevices[device].length < this.devices[device].length);
			const [added] = Object.keys(updatedDevices).filter((device) => {
				if (updatedDevices[device].length > this.devices[device].length) {
					deviceId = updatedDevices[device].filter((d) => !this.devices[device].find((e) => e.deviceId === d.deviceId))[0]
						.deviceId;

					return true;
				}

				return false;
			});

			if (typeof added !== 'undefined') {
				status = added;
				statusState = 'added';
			}

			if (typeof removed !== 'undefined') {
				status = removed;
				statusState = 'removed';
			}
			if (this.mediaStream !== null) {
				switch (status) {
					case 'videoinput':
						this.chosenVideoinputDeviceId = deviceId;
						this.onUpdateActiveDevices.emit({ device: status, state: statusState as any });

						break;
					case 'audioinput':
						this.chosenAudioinputDeviceId = deviceId;
						this.onUpdateActiveDevices.emit({ device: status, state: statusState as any });

						break;
					case 'audiooutput':
						this.chosenAudiooutputDeviceId = deviceId;
						this.onUpdateActiveDevices.emit({ device: status, state: statusState as any });

						break;
				}
			}

			this.devices = updatedDevices;
			this.onDeviceChange.emit();
		} catch (error) {
			this.parseError(error);
		}
	}

	public parseError(error: any, id = 'Generic', popup = true): { code: string; translated: string; source: string } {
		let message = '';
		const { name } = error;
		const messagePrefix = 'Common.Devices.Error.';

		switch (name) {
			/* 		case 'NotFoundError':
				message = `${messagePrefix}${id}.NotFound`;

				break;
			case 'DevicesNotFoundError':
				message = `${messagePrefix}${id}.NotFound`;

				break; */
			/* 			case 'NotReadableError':
				message = `${messagePrefix}${id}.SourceUnavailable`;

				break; */
			/* 		case 'SourceUnavailableError':
				message = `${messagePrefix}${id}.SourceUnavailable`;

				break; */
			case 'PermissionDeniedError':
				message = `${messagePrefix}${id}.PermissionDenied`;

				break;
			case 'NotAllowedError':
				message = `${messagePrefix}${id}.PermissionDenied`;

				break;
			case 'SecurityError':
				message = `${messagePrefix}${id}.PermissionDenied`;

				break;
			/* 		case 'OverconstrainedError':
				message = `${messagePrefix}${id}.Overconstrained`;

				break; */
			/* 			case 'SecurityError':
				message = 'You need to use HTTPS for selecting audio output';

				break; */
			default:
				message = `${messagePrefix}${id}.Unsorted`;

				break;
		}

		const errorMessage = {
			translated: message,
			code: name,
			source: id,
		};

		// These are the errors currently replaced in new patient web/retina. Suppress them.
		// This is a quick and dirty way of disabling these until the whole file can be deleted.
		const errorsReplacedInNewCall = ['NotFoundError', 'DevicesNotFoundError'];

		if (popup && !errorsReplacedInNewCall.some((error) => error === name)) {
			this.addPopup(errorMessage);
		}

		this._spaLogger.log(
			'Debug',
			['Public'],
			`${error.name}: ${id} | ${error['message']}`,
			{ Url: window.location.href, LicensHolderId: this._spaContext.unitConfig.LicenceHolder.Id, ErrorType: error.name },
			error.stack,
		);

		return errorMessage;
	}

	public closeStreams() {
		if (!this.mediaStream) return;

		this.mediaStream.getTracks().forEach((track) => {
			track.onended = null;
			track.stop();
		});

		this.mediaStream = null;
		this.newStreamSessionID();
	}

	public clearPopups() {
		while (this.popupList.length) {
			let popup = this.popupList.pop();
			popup.destroy();
		}
	}

	private addPopup(message: { code: string; translated: string; source: string }) {
		const popup = new VisibaPopup(
			this.translateService.get(`Common.Devices.Error.${message.source}`),
			this.translateService.get(message.translated),
		)
			.persistant()
			.inform(this.translateService.get('Common.Button.OK'));

		this.popupList.push(popup);

		if (this.popupList.length === 1) {
			popup
				.attatch()
				.observer()
				.subscribe((ok) => {
					this.nextPopup();
				});
		}
	}

	private getCameraConstraints(exactID: string | null) {
		if (exactID === null || typeof exactID === 'undefined') exactID = 'default';

		const trackConstraints: MediaTrackConstraints = {
			width: { max: 1280 },
			height: { max: 720 },
			deviceId: exactID !== 'default' ? { exact: exactID } : undefined,
		};

		return trackConstraints;
	}

	private getAudioConstraints(exactID: string | null) {
		if (exactID === null || typeof exactID === 'undefined') exactID = 'default';

		const trackConstraints: MediaTrackConstraints = {
			deviceId: exactID !== 'default' ? { exact: exactID } : undefined,
		};

		return trackConstraints;
	}

	private nextPopup() {
		const popup = this.popupList.shift();

		if (this.popupList.length > 0) {
			this.popupList[0]
				.attatch()
				.observer()
				.subscribe((ok) => {
					this.nextPopup();
				});
		}
	}
}
